/**
 * Main Entry Point - Erinnern & Leben
 * ====================================
 * ES6 Module-based architecture with TypeScript
 */

// Global CSS (bundled via esbuild)

// Modules
import { initSplashScreenEarly } from './modules/splash-screen';
import { initMobileNav } from './modules/mobile-nav';
import { initVideoPlayers, FULLSCREEN_OPTIONS, lockLandscapeInFullscreen } from './modules/video-players';
import { initExpandables } from './modules/expandables';
import { loadData } from './modules/data-loader';
import { initSmoothScroll } from './modules/smooth-scroll';
import { initScrollToTop } from './modules/scroll-to-top';
import { initAccessibility } from './modules/accessibility';
import { initBackNavigation, handleReturnScroll } from './modules/back-navigation';
import { initScrollMemory, restoreScrollMemory } from './modules/scroll-memory';
import { initEmailProtection } from './modules/email-protection';
import { initStickyMap } from './modules/sticky-map';
import { initCopyToClipboard } from './modules/copy-to-clipboard';
// @ts-expect-error - Plyr default export types don't match ES module import
import Plyr from 'plyr';

// Components
import { initCarousels } from './components/carousel';
import { initLightbox } from './components/lightbox';
import { VideoChapters, initVideoChapters } from './components/video-chapters';
import { Carousel } from './components/carousel';
import { Lightbox } from './components/lightbox';

export type { VideoChapter, LightboxImage } from './types';

/**
 * Initialize all application modules
 */
function initApp(): void {
  initMobileNav();
  initVideoPlayers();
  initExpandables();
  loadData();
  initSmoothScroll();
  initScrollToTop();
  initAccessibility();
  initBackNavigation();
  initScrollMemory();
  if (!restoreScrollMemory()) {
    handleReturnScroll();
  }
  initEmailProtection();
  initStickyMap();
  initCopyToClipboard();

  // Initialize components
  initCarousels();
  initLightbox();
}

// Initialize splash screen immediately (before DOMContentLoaded)
// This prevents flash of content
initSplashScreenEarly();

// Initialize app when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  initApp();
}

// Export classes and functions for use in inline scripts
// This allows pages to use these components without importing
export { Carousel } from './components/carousel';
export { Lightbox } from './components/lightbox';
export { VideoChapters, initVideoChapters } from './components/video-chapters';

// For backwards compatibility, expose to window object
// This can be removed once all pages are updated to use ES modules
declare global {
  interface Window {
    App: typeof AppNamespace;
    Carousel: typeof Carousel;
    Lightbox: typeof Lightbox;
    VideoChapters: typeof VideoChapters;
    initVideoChapters: typeof initVideoChapters;
    Plyr: typeof Plyr;
  }
}

const AppNamespace = {
  Carousel,
  Lightbox,
  VideoChapters,
  initVideoChapters,
  FULLSCREEN_OPTIONS,
  lockLandscapeInFullscreen,
};

window.App = AppNamespace;
window.Carousel = Carousel;
window.Lightbox = Lightbox;
window.VideoChapters = VideoChapters;
window.initVideoChapters = initVideoChapters;
window.Plyr = Plyr;

import '../css/fonts.css';
import '../css/style.css';
